import {
  bucketUnitFor,
  changeRatio,
  completedIn,
  earningsByVehicle,
  earningsIn,
  earningsSeries,
  occupancyIn,
  precedingWindow,
  statusMix,
  windowOf,
  type BucketUnit,
  type MetricWindow,
} from '@zuruflex/core';
import {
  formatDateIn,
  formatDateTimeIn,
  formatMoneyCompactIn,
  formatMoneyIn,
  toLocale,
  type Locale,
} from '@zuruflex/i18n';
import {
  Alert,
  Badge,
  BarChart,
  BarList,
  Button,
  DonutChart,
  EmptyState,
  ErrorState,
  ListRow,
  Meter,
  Money,
  PageHeader,
  Panel,
  PanelEmpty,
  Segmented,
  Sparkline,
  StatGrid,
  StatTile,
  TrendPill,
  type BarDatum,
  type DonutSegment,
} from '@zuruflex/ui';
import { useQuery } from '@tanstack/react-query';
import { CalendarCheck, Car, Gauge, Wallet } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import { usePartner } from '../lib/auth.js';
import { fetchHires, type HireRow } from '../lib/hires.js';

/** Enough to see what is coming without leaving the page. */
const UPCOMING_SHOWN = 5;

/** Past this, the queue is a list to work through, not a to-do on a dashboard. */
const NEEDS_YOU_SHOWN = 3;

/** A leaderboard, not a fleet list. The fleet page is the fleet list. */
const TOP_CARS_SHOWN = 5;

/**
 * The periods the console can be read over, as the radio group's values —
 * strings, because that is what a radio input carries. `Number(range)` is
 * the span in days, and the same literal keys the label in `partner.json`.
 */
const RANGES = ['30', '90', '365'] as const;
type Range = (typeof RANGES)[number];

/**
 * The statuses of a hire that is still going somewhere.
 *
 * The ring shows the open book rather than every hire ever taken: on any
 * established fleet COMPLETED is nine tenths of the rows, and a donut that
 * is nine tenths one colour has drawn a fact the owner already knew instead
 * of the four numbers they can still do something about.
 */
const OPEN_STATUSES = ['PENDING', 'AWAITING_PAYMENT', 'CONFIRMED', 'ACTIVE'] as const;

const OPEN_COLOURS: Record<(typeof OPEN_STATUSES)[number], string> = {
  PENDING: 'text-warning',
  AWAITING_PAYMENT: 'text-info',
  CONFIRMED: 'text-accent',
  ACTIVE: 'text-success',
};

/**
 * How a bucket is labelled on the axis, and how it reads in the tooltip.
 *
 * The axis gets the shortest thing that stays unambiguous across seven
 * labels — a bare day number under a month of bars, a month name under a
 * year of them — and the tooltip gets the date in full, because that is
 * where a partner goes when the shape has already caught their eye.
 */
function labelBucket(
  start: Date,
  unit: BucketUnit,
  locale: Locale,
  weekOf: (date: string) => string,
): { label: string; caption: string } {
  const tag = locale === 'en' ? 'en-GB' : locale;

  if (unit === 'month') {
    return {
      label: new Intl.DateTimeFormat(tag, { month: 'short' }).format(start),
      caption: new Intl.DateTimeFormat(tag, { month: 'long', year: 'numeric' }).format(start),
    };
  }

  const short = new Intl.DateTimeFormat(tag, { day: 'numeric', month: 'short' }).format(start);

  if (unit === 'week') {
    return { label: short, caption: weekOf(formatDateIn(start, locale)) };
  }

  return {
    label: new Intl.DateTimeFormat(tag, { day: 'numeric' }).format(start),
    caption: formatDateIn(start, locale),
  };
}

/**
 * The owner's console.
 *
 * Three bands, in the order the questions get asked: how did the last
 * period go, what does the trend look like and where is the money coming
 * from, and then what is waiting on me right now. The period switcher at
 * the top drives the whole first half — a delta and a chart measured over
 * different spans are two answers to two questions nobody asked.
 */
export function OverviewPage() {
  const { t, i18n } = useTranslation(['partner', 'common']);
  const locale = toLocale(i18n.language);
  const { user, partner } = usePartner();
  const [range, setRange] = useState<Range>('30');
  const days = Number(range);

  const fleet = useQuery({
    queryKey: ['my-fleet'],
    queryFn: () => api.listVehicles({ partnerId: partner.id, perPage: 100 }),
  });

  const hires = useQuery({
    queryKey: ['my-hires'],
    queryFn: fetchHires,
  });

  const cars = useMemo(() => fleet.data?.items ?? [], [fleet.data]);
  const book = useMemo<HireRow[]>(() => hires.data ?? [], [hires.data]);

  const live = cars.filter((car) => car.listingStatus === 'PUBLISHED');
  const notLive = cars.filter((car) => car.listingStatus !== 'PUBLISHED');
  const rejected = cars.filter((car) => car.listingStatus === 'REJECTED');

  const money = (amount: number) => formatMoneyIn(amount, partner.currency, locale);

  /*
   * One `now` for every figure on the page.
   *
   * Calling `new Date()` inside each rollup would let a render that lands
   * either side of midnight put the tiles in one window and the chart in
   * the next — rare, and impossible to reproduce when it is reported.
   */
  const metrics = useMemo(() => {
    const now = new Date();
    const window: MetricWindow = windowOf(days, now);
    const before = precedingWindow(window);
    const unit = bucketUnitFor(window);

    const earned = earningsIn(book, window);
    const done = completedIn(book, window);
    const occupancy = occupancyIn(book, window, live.length);
    const occupancyBefore = occupancyIn(book, before, live.length);

    return {
      window,
      unit,
      earned,
      earnedChange: changeRatio(earned, earningsIn(book, before)),
      done,
      doneChange: changeRatio(done, completedIn(book, before)),
      occupancy,
      occupancyChange:
        occupancy === null || occupancyBefore === null
          ? null
          : changeRatio(occupancy, occupancyBefore),
      allTime: book.reduce(
        (total, hire) => (hire.status === 'COMPLETED' ? total + hire.partnerEarnings : total),
        0,
      ),
      series: earningsSeries(book, window, unit),
      byVehicle: earningsByVehicle(book, window).slice(0, TOP_CARS_SHOWN),
      mix: statusMix(book),
    };
  }, [book, days, live.length]);

  const bars = useMemo<BarDatum[]>(
    () =>
      metrics.series.map((bucket) => ({
        key: bucket.start.toISOString(),
        value: bucket.value,
        ...labelBucket(bucket.start, metrics.unit, locale, (date) =>
          t('overview.weekOf', { date }),
        ),
      })),
    [metrics.series, metrics.unit, locale, t],
  );

  const openSegments = useMemo<DonutSegment[]>(
    () =>
      OPEN_STATUSES.map((status) => ({
        key: status,
        label: t(`common:bookingStatus.${status}`),
        value: metrics.mix[status],
        colorClassName: OPEN_COLOURS[status],
      })),
    [metrics.mix, t],
  );

  const openTotal = openSegments.reduce((total, segment) => total + segment.value, 0);

  /** `${year} ${make} ${model}` for a car this partner owns, by id. */
  const carNames = useMemo(() => {
    const names = new Map<string, { name: string; plate: string }>();
    for (const car of cars) {
      names.set(car.id, { name: `${car.year} ${car.make} ${car.model}`, plate: car.plateNumber });
    }
    return names;
  }, [cars]);

  const now = Date.now();

  const upcoming = book
    .filter((hire) => new Date(hire.pickupAt).getTime() > now && hire.status !== 'CANCELLED')
    .sort((a, b) => new Date(a.pickupAt).getTime() - new Date(b.pickupAt).getTime());

  /**
   * Reservations this partner has not answered yet — the one urgent queue.
   *
   * Taken from every hire rather than from `upcoming`, because a request
   * whose pick-up date has already gone past is the most urgent thing on
   * the page, not the least. Filtering it out with the rest of the past
   * hid the only row anybody had to act on.
   */
  const pending = book
    .filter((hire) => hire.status === 'PENDING')
    .sort((a, b) => new Date(a.pickupAt).getTime() - new Date(b.pickupAt).getTime());

  const hireName = (hire: HireRow) =>
    hire.vehicle ? `${hire.vehicle.make} ${hire.vehicle.model}` : hire.reference;

  const percent = (fraction: number) =>
    new Intl.NumberFormat(locale === 'en' ? 'en-GB' : locale, {
      style: 'percent',
      maximumFractionDigits: 0,
    }).format(fraction);

  const periodCaption = t('overview.vsPrevious', { days });
  const isPending = hires.isPending || fleet.isPending;

  if (hires.isError || fleet.isError) {
    return (
      <>
        <PageHeader
          title={t('overview.title')}
          description={t('overview.welcome', { name: user.fullName.split(' ')[0] })}
        />
        <ErrorState
          error={hires.error ?? fleet.error}
          onRetry={() => {
            void hires.refetch();
            void fleet.refetch();
          }}
        />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('overview.title')}
        description={t('overview.welcome', { name: user.fullName.split(' ')[0] })}
        action={
          <Segmented
            label={t('overview.rangeLabel')}
            value={range}
            onChange={setRange}
            options={RANGES.map((option) => ({
              value: option,
              label: t(`overview.range.${option}`),
            }))}
          />
        }
      />

      {rejected.length > 0 ? (
        <Alert variant="warning" className="mb-6">
          <span className="font-semibold">{t('overview.rejectedTitle')}</span>{' '}
          {rejected[0]?.reviewNote}{' '}
          <Link to="/fleet" className="underline underline-offset-2">
            {t('nav.fleet')}
          </Link>
        </Alert>
      ) : null}

      <StatGrid>
        <StatTile
          accent
          label={t('overview.earnedIn')}
          value={<Money amount={metrics.earned} currency={partner.currency} />}
          hint={periodCaption}
          icon={<Wallet className="size-4" />}
          isLoading={isPending}
          trend={<TrendPill ratio={metrics.earnedChange} caption={periodCaption} />}
          footer={
            <Sparkline
              tone="inherit"
              values={metrics.series.map((bucket) => bucket.value)}
              label={t('overview.earningsTitle')}
              className="text-accent-text/70"
            />
          }
          to="/bookings"
        />

        <StatTile
          label={t('overview.hiresDone')}
          value={String(metrics.done)}
          hint={periodCaption}
          icon={<CalendarCheck className="size-4" />}
          isLoading={isPending}
          trend={<TrendPill ratio={metrics.doneChange} caption={periodCaption} />}
          to="/bookings"
        />

        <StatTile
          label={t('overview.occupancy')}
          value={metrics.occupancy === null ? '—' : percent(metrics.occupancy)}
          hint={t('overview.occupancyHint')}
          icon={<Gauge className="size-4" />}
          isLoading={isPending}
          trend={<TrendPill ratio={metrics.occupancyChange} caption={periodCaption} />}
          footer={
            metrics.occupancy === null ? null : (
              <Meter
                compact
                fraction={metrics.occupancy}
                label={t('overview.occupancy')}
                value={percent(metrics.occupancy)}
              />
            )
          }
          to="/fleet"
        />

        <StatTile
          label={t('overview.live')}
          value={String(live.length)}
          hint={
            notLive.length > 0
              ? t('overview.notLiveHint', { count: notLive.length })
              : t('overview.allLive')
          }
          icon={<Car className="size-4" />}
          isLoading={isPending}
          to="/fleet"
        />
      </StatGrid>

      {!fleet.isPending && cars.length === 0 ? (
        <EmptyState
          className="mt-8"
          title={t('overview.noCars')}
          description={t('overview.noCarsBody')}
          action={
            <Button asChild>
              <Link to="/fleet">{t('overview.addCar')}</Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="mt-6 grid gap-4 xl:grid-cols-3">
            <Panel
              className="xl:col-span-2"
              title={t('overview.earningsTitle')}
              subtitle={t('overview.allTime', { amount: money(metrics.allTime) })}
              isLoading={isPending}
            >
              <BarChart
                data={bars}
                label={t('overview.earningsTitle')}
                format={(value) => formatMoneyCompactIn(value, partner.currency, locale)}
                emptyLabel={t('overview.earningsEmpty')}
              />
            </Panel>

            <Panel title={t('overview.openTitle')} isLoading={isPending}>
              {openTotal === 0 ? (
                <PanelEmpty>{t('overview.openEmpty')}</PanelEmpty>
              ) : (
                <DonutChart
                  segments={openSegments}
                  label={t('overview.openTitle')}
                  centerValue={String(openTotal)}
                  centerLabel={t('overview.openCenter')}
                />
              )}
            </Panel>
          </div>

          <div className="mt-4 grid gap-4 xl:grid-cols-3">
            <Panel
              title={t('overview.needsYou')}
              subtitle={
                pending.length + notLive.length > 0
                  ? String(pending.length + notLive.length)
                  : undefined
              }
              isLoading={isPending}
            >
              {pending.length === 0 && notLive.length === 0 ? (
                <PanelEmpty>{t('overview.allClear')}</PanelEmpty>
              ) : (
                <ul className="flex flex-col gap-2">
                  {/*
                    Named one by one rather than summed into "1 booking to
                    confirm". The count told a partner there was something to
                    do and then made them go and find out what.
                  */}
                  {pending.slice(0, NEEDS_YOU_SHOWN).map((hire) => (
                    <ListRow
                      key={hire.id}
                      to={`/bookings?ref=${encodeURIComponent(hire.reference)}`}
                      title={hireName(hire)}
                      meta={t('overview.awaitingYou', {
                        date: formatDateTimeIn(hire.pickupAt, locale),
                      })}
                      trailing={
                        <Badge variant="warning">{t('common:bookingStatus.PENDING')}</Badge>
                      }
                    />
                  ))}

                  {pending.length > NEEDS_YOU_SHOWN ? (
                    <li>
                      <Link
                        to="/bookings?tab=needs"
                        className="inline-block text-sm font-medium underline underline-offset-2"
                      >
                        {t('overview.toConfirm', { count: pending.length })}
                      </Link>
                    </li>
                  ) : null}

                  {notLive.map((car) => (
                    <ListRow
                      key={car.id}
                      to={`/fleet/${car.id}`}
                      title={`${car.year} ${car.make} ${car.model}`}
                      meta={car.reviewNote ?? t(`common:listingStatus.${car.listingStatus}`)}
                    />
                  ))}
                </ul>
              )}
            </Panel>

            <Panel
              title={t('overview.nextOut')}
              action={
                upcoming.length > UPCOMING_SHOWN ? (
                  <Link
                    to="/bookings"
                    className="text-sm font-medium underline underline-offset-2"
                  >
                    {t('overview.seeAllBookings', { count: upcoming.length })}
                  </Link>
                ) : null
              }
              isLoading={isPending}
            >
              {upcoming.length === 0 ? (
                <PanelEmpty>{t('overview.nothingBooked')}</PanelEmpty>
              ) : (
                <ul className="flex flex-col gap-2">
                  {upcoming.slice(0, UPCOMING_SHOWN).map((hire) => (
                    <ListRow
                      key={hire.id}
                      to={`/bookings?ref=${encodeURIComponent(hire.reference)}`}
                      title={hireName(hire)}
                      meta={formatDateTimeIn(hire.pickupAt, locale)}
                      trailing={
                        <Badge variant={hire.status === 'PENDING' ? 'warning' : 'info'}>
                          {t(`common:bookingStatus.${hire.status}` as const)}
                        </Badge>
                      }
                    />
                  ))}
                </ul>
              )}
            </Panel>

            <Panel
              title={t('overview.topCars')}
              subtitle={t(`overview.range.${range}`)}
              isLoading={isPending}
            >
              {metrics.byVehicle.length === 0 ? (
                <PanelEmpty>{t('overview.topCarsEmpty')}</PanelEmpty>
              ) : (
                <BarList
                  items={metrics.byVehicle.map((row) => {
                    const car = carNames.get(row.vehicleId);
                    return {
                      key: row.vehicleId,
                      label: car?.name ?? t('overview.carGone'),
                      meta: [car?.plate, t('overview.hireCount', { count: row.hires })]
                        .filter(Boolean)
                        .join(' · '),
                      value: row.amount,
                      to: car ? `/fleet/${row.vehicleId}` : undefined,
                    };
                  })}
                  format={money}
                />
              )}
            </Panel>
          </div>
        </>
      )}
    </>
  );
}
